import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationsService, IntranetNotification } from '../../services/notifications.service';
import { useFeedUnreadDots } from '../../hooks/useFeedUnreadDots';
import { isAdminRole, isCoAdminOrAdmin } from '../../utils/roles';
import './Header.css';

/** Format a date as relative time ("just now", "5 min ago", etc.) */
const timeAgo = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

/** Fire a browser desktop notification (uses the global browser Notification API) */
const fireDesktopNotification = (title: string, body: string) => {
  if (!('Notification' in window) || (window as any).Notification.permission !== 'granted') return;
  try {
    new (window as any).Notification(title, {
      body,
      icon: '/toyota-lanka-logo.png',
      badge: '/toyota-lanka-logo.png',
    });
  } catch (_) {}
};

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<IntranetNotification[]>([]);
  const [activeTab, setActiveTab] = useState<'all' | 'unread' | 'read'>('all');
  const [toast, setToast] = useState<{ title: string; message?: string } | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevIdsRef = useRef<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => location.pathname === path;
  const feed = new URLSearchParams(location.search).get('feed');
  const isFeedHome =
    location.pathname === '/' &&
    (feed === null || feed === '' || feed === 'home');
  const isFeedBranch = location.pathname === '/' && feed === 'branch';
  const isFeedDepartment = location.pathname === '/' && feed === 'department';
  const isAdmin = isAdminRole(user?.role);
  const showCoAdminNav = isCoAdminOrAdmin(user?.role);
  const {
    branchUnread,
    departmentUnread,
    onBranchNavClick,
    onDepartmentNavClick,
  } = useFeedUnreadDots();

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const readCount = notifications.filter(n => n.isRead).length;
  const totalCount = notifications.length;

  // Request browser notification permission once on mount
  useEffect(() => {
    if ('Notification' in window && (window as any).Notification.permission === 'default') {
      (window as any).Notification.requestPermission();
    }
  }, []);

  const showToast = useCallback((title: string, message?: string) => {
    setToast({ title, message });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  const loadNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const notifs = await notificationsService.getAll();
      const unique = Array.from(new Map(notifs.map(n => [n.id, n])).values());

      // Detect newly arrived notifications
      const incoming = unique.filter(n => !n.isRead && !prevIdsRef.current.has(n.id));
      if (prevIdsRef.current.size > 0 && incoming.length > 0) {
        const first = incoming[0];
        fireDesktopNotification(first.title, first.message || '');
        showToast(first.title, first.message);
      }
      prevIdsRef.current = new Set(unique.map(n => n.id));

      setNotifications(unique);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  }, [user, showToast]);

  // Initial load + poll every 10 seconds
  useEffect(() => {
    if (user) {
      loadNotifications();
      const interval = setInterval(loadNotifications, 10000);
      return () => clearInterval(interval);
    }
  }, [user, loadNotifications]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node))
        setShowDropdown(false);
      if (notificationsRef.current && !notificationsRef.current.contains(e.target as Node))
        setShowNotifications(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent background page scrolling while notifications panel is open.
  useEffect(() => {
    if (!showNotifications) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.paddingRight = previousBodyPaddingRight;
    };
  }, [showNotifications]);

  useEffect(() => { setShowDropdown(false); }, [location]);

  const handleNotificationClick = async (notification: IntranetNotification) => {
    if (!notification.isRead) {
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
      );
      await notificationsService.markAsRead(notification.id);
    }
    if (notification.postId) {
      navigate('/', { state: { scrollToPost: notification.postId } });
    }
  };

  const handleMarkAllRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      await notificationsService.markAllAsRead();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      loadNotifications();
    }
  };

  const displayedNotifications = notifications.filter(n => {
    if (activeTab === 'unread') return !n.isRead;
    if (activeTab === 'read') return n.isRead;
    return true;
  });

  return (
    <header className="header">
      {/* In-app toast */}
      {toast && (
        <div className="notif-toast" onClick={() => setToast(null)}>
          <div className="notif-toast-icon">🔔</div>
          <div className="notif-toast-body">
            <div className="notif-toast-title">{toast.title}</div>
            {toast.message && <div className="notif-toast-msg">{toast.message}</div>}
          </div>
          <button className="notif-toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      <div className="header-left">
        <Link to="/" className="header-logo-link">
          <img src="/toyota-lanka-logo.png" alt="Toyota Lanka" className="header-logo-img" />
        </Link>
      </div>

      <nav className="header-nav">
        <Link to="/" className={`nav-link ${isActive('/') && isFeedHome ? 'active' : ''}`}>
          Home
        </Link>
        <Link
          to="/?feed=branch"
          className={`nav-link nav-link-feed ${isActive('/') && isFeedBranch ? 'active' : ''}`}
          title="My Branch feed"
          onClick={onBranchNavClick}
        >
          <span className="nav-feed-title">My Branch</span>
          {branchUnread && (
            <span className="feed-nav-unread-dot" aria-hidden title="New posts in My Branch" />
          )}
        </Link>
        <Link
          to="/?feed=department"
          className={`nav-link nav-link-feed ${isActive('/') && isFeedDepartment ? 'active' : ''}`}
          title="My Department feed"
          onClick={onDepartmentNavClick}
        >
          <span className="nav-feed-title">My Department</span>
          {departmentUnread && (
            <span className="feed-nav-unread-dot" aria-hidden title="New posts in My Department" />
          )}
        </Link>
        <Link to="/documents" className={`nav-link ${isActive('/documents') ? 'active' : ''}`}>Documents / Policies / SOPs</Link>
        {showCoAdminNav && (
          <Link to="/users" className={`nav-link ${isActive('/users') ? 'active' : ''}`}>
            Users
          </Link>
        )}
        {isAdmin && (
          <>
            <Link to="/admin/analytics" className={`nav-link ${isActive('/admin/analytics') ? 'active' : ''}`}>
              Analytics
            </Link>
            <Link to="/admin/leads" className={`nav-link ${isActive('/admin/leads') ? 'active' : ''}`}>
              Vehicle Leads
            </Link>
            <Link to="/admin/activity" className={`nav-link ${isActive('/admin/activity') ? 'active' : ''}`}>
              Activity log
            </Link>
          </>
        )}
      </nav>

      <div className="header-right">
        {/* Notifications */}
        <div className="notifications-container" ref={notificationsRef}>
          <button
            className={`notification-bell ${unreadCount > 0 ? 'has-unread' : ''}`}
            onClick={() => setShowNotifications(!showNotifications)}
            aria-label="Notifications"
          >
            🔔
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </button>

          {showNotifications && (
            <div className="notifications-dropdown">
              {/* Header */}
              <div className="notifications-header">
                <h3>Notifications</h3>
                {unreadCount > 0 && (
                  <button className="mark-all-read" onClick={handleMarkAllRead} type="button">
                    Mark all read
                  </button>
                )}
              </div>

              {/* Stats bar */}
              <div className="notifications-stats">
                <button
                  className={`notif-stat-btn ${activeTab === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveTab('all')}
                >
                  <span className="notif-stat-num">{totalCount}</span>
                  <span className="notif-stat-label">Total</span>
                </button>
                <div className="notif-stat-divider" />
                <button
                  className={`notif-stat-btn ${activeTab === 'unread' ? 'active' : ''}`}
                  onClick={() => setActiveTab('unread')}
                >
                  <span className="notif-stat-num unread-num">{unreadCount}</span>
                  <span className="notif-stat-label">Unread</span>
                </button>
                <div className="notif-stat-divider" />
                <button
                  className={`notif-stat-btn ${activeTab === 'read' ? 'active' : ''}`}
                  onClick={() => setActiveTab('read')}
                >
                  <span className="notif-stat-num read-num">{readCount}</span>
                  <span className="notif-stat-label">Read</span>
                </button>
              </div>

              {/* List */}
              <div className="notifications-list">
                {displayedNotifications.length === 0 ? (
                  <div className="no-notifications">
                    <span className="no-notif-icon">🔕</span>
                    <span>
                      {activeTab === 'unread' ? 'No unread notifications' :
                       activeTab === 'read'   ? 'No read notifications' :
                                                'No notifications yet'}
                    </span>
                  </div>
                ) : (
                  displayedNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`notification-item ${notification.isRead ? 'read' : 'unread'}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      {!notification.isRead && <span className="notif-dot" />}
                      <div className="notification-content">
                        <h4>{notification.title}</h4>
                        {notification.message && (
                          <p>
                            {notification.message.length > 100
                              ? notification.message.slice(0, 100) + '…'
                              : notification.message}
                          </p>
                        )}
                        <span className="notification-time">{timeAgo(notification.createdAt)}</span>
                      </div>
                      {notification.isRead && (
                        <span className="notif-read-tick" title="Read">✓</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="user-menu-container" ref={dropdownRef}>
          <div className="user-menu-trigger" onClick={() => setShowDropdown(!showDropdown)}>
            <div className="user-avatar-small">
              {user?.profilePicture ? (
                <img src={user.profilePicture} alt={user.fullName} />
              ) : (
                user?.fullName?.charAt(0) || 'U'
              )}
            </div>
            <span className="user-name">{user?.fullName}</span>
            <span className={`dropdown-arrow ${showDropdown ? 'open' : ''}`}>▼</span>
          </div>

          {showDropdown && (
            <div className="user-dropdown">
              <Link to="/profile" className="dropdown-item" onClick={() => setShowDropdown(false)}>
                Profile Settings
              </Link>
              {showCoAdminNav && (
                <Link to="/users" className="dropdown-item" onClick={() => setShowDropdown(false)}>
                  User Management
                </Link>
              )}
              {isAdmin && (
                <>
                  <Link to="/admin/analytics" className="dropdown-item" onClick={() => setShowDropdown(false)}>
                    Analytics Dashboard
                  </Link>
                  <Link to="/admin/leads" className="dropdown-item" onClick={() => setShowDropdown(false)}>
                    Vehicle Leads
                  </Link>
                  <Link to="/admin/activity" className="dropdown-item" onClick={() => setShowDropdown(false)}>
                    Activity log
                  </Link>
                </>
              )}
              <div className="dropdown-divider"></div>
              <button onClick={() => { setShowDropdown(false); logout(); }} className="dropdown-item logout">
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
